/* eslint-disable no-console */
/**
 * i18n URL 缓存工具
 *
 * 单层缓存策略：
 * - CDN URL 缓存（中间件层）：缓存 TranslationAjax 返回的 CDN URL
 * - 翻译数据本身不缓存，直接从 CDN 获取（利用 CDN 的边缘缓存）
 */

const { CACHE_CONFIG } = require('./constants');

// URL 缓存存储（只缓存 CDN URL）
const urlCache = new Map();

/**
 * 生成缓存 key
 * @param {number|string} routeId - 路由ID
 * @param {string} lang - 语言
 * @returns {string} 缓存key
 */
function getCacheKey(routeId, lang) {
  return `i18n:${routeId}:${lang}`;
}

/**
 * 从 URL 缓存获取数据（中间件使用）
 * @param {number|string} routeId - 路由ID
 * @param {string} lang - 语言
 * @returns {string|null} CDN URL 或 null
 */
function getUrlCache(routeId, lang) {
  const key = getCacheKey(routeId, lang);
  const cached = urlCache.get(key);

  if (!cached) {
    return null;
  }

  const { data, timestamp } = cached;
  const now = Date.now();

  // 检查是否过期
  if (now - timestamp > CACHE_CONFIG.URL_TTL) {
    urlCache.delete(key);
    console.log(`[i18nCache] URL cache expired for ${key}`);
    return null;
  }

  console.log(`[i18nCache] URL cache hit for ${key}`);
  return data;
}

/**
 * 设置 URL 缓存（中间件使用）
 * @param {number|string} routeId - 路由ID
 * @param {string} lang - 语言
 * @param {string} url - CDN URL
 */
function setUrlCache(routeId, lang, url) {
  const key = getCacheKey(routeId, lang);

  urlCache.set(key, {
    data: url,
    timestamp: Date.now(),
  });

  console.log(`[i18nCache] URL cache set for ${key}, total: ${urlCache.size}`);

  // 如果缓存过多，触发清理
  if (urlCache.size > CACHE_CONFIG.CLEANUP_THRESHOLD) {
    cleanupCache();
  }
}

/**
 * 清理过期缓存
 */
function cleanupCache() {
  const now = Date.now();
  let deletedCount = 0;

  for (const [key, value] of urlCache.entries()) {
    if (now - value.timestamp > CACHE_CONFIG.URL_TTL) {
      urlCache.delete(key);
      deletedCount = deletedCount + 1;
    }
  }

  console.log(`[i18nCache] URL cleanup: deleted ${deletedCount} expired entries, remaining: ${urlCache.size}`);

  // 如果清理后还是超过最大值，删除最旧的
  if (urlCache.size > CACHE_CONFIG.MAX_SIZE) {
    const entries = Array.from(urlCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    const toDelete = urlCache.size - CACHE_CONFIG.MAX_SIZE;
    for (let i = 0; i < toDelete; i++) {
      urlCache.delete(entries[i][0]);
    }

    console.log(`[i18nCache] URL: removed ${toDelete} oldest entries to keep size under limit`);
  }
}

/**
 * 清空所有缓存
 */
function clearCache() {
  const { size } = urlCache;
  urlCache.clear();
  console.log(`[i18nCache] Cleared URL cache (${size} entries)`);
}

/**
 * 获取缓存统计信息
 */
function getCacheStats() {
  return {
    urlCache: {
      size: urlCache.size,
      maxSize: CACHE_CONFIG.MAX_SIZE,
      ttl: CACHE_CONFIG.URL_TTL,
    },
  };
}

module.exports = {
  getUrlCache,
  setUrlCache,
  clearCache,
  getCacheStats,
};
